// React and MUI imports for hooks, layout, and UI
import React, { useState, useEffect } from 'react';
import {
  Box, // MUI layout component
  Typography, // MUI text component
  Container, // MUI container for responsive width
  useMediaQuery, // MUI hook for responsive design
  useTheme, // MUI hook for theme access
  CssBaseline, // MUI component to normalize CSS
  styled // MUI utility for custom styled components
} from '@mui/material';
import Header from '../../commons/Header'; // App header
import Footer from '../../commons/Footer'; // App footer
import HDButton from '../../hd-ui-kit/HDButton'; // Custom button
import ArrowForwardSharpIcon from '@mui/icons-material/ArrowForwardSharp'; // Arrow icon
import HDTextField from '../../hd-ui-kit/HDTextField'; // Custom text field
import { useNavigate } from 'react-router-dom'; // React Router navigation
import Api from '../../Api'; // API utility for backend calls
import Loader from '../../commons/Loader'; // Loading overlay
import { validateFields, validationOptions } from '../../ValidationSchema'; // Validation helpers
import { reactGAEvents, backgroundTopRight, backgroundTopRightDark, titleStyle, styledText, styledBoxMainWrapper, styledDividerLeft } from '../../commons/commonFunctions'; // Common style and analytics helpers

/**
 * ForgetPassword
 * Page for users to request a password reset link by entering their email address.
 * Handles validation, API call, and navigation to reset confirmation.
 */
const ForgetPassword = () => {
  // Access the current MUI theme object
  const theme = useTheme();
  // Determine if the screen is mobile size
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // React Router navigation function
  const navigate = useNavigate();
  // State for email input
  const [email, setEmail] = useState('');
  // State for validation errors
  const [errors, setErrors] = useState<any>({});
  // State for loading overlay
  const [loading, setLoading] = useState<boolean>(false);
  // Current theme mode ('light' or 'dark')
  const themeMode = theme.palette.mode;

  // Style for the main vertical flex box containing the title and instructions
  const MainStyledBox = {
    display: 'flex',
    flexDirection: 'column',
  };

  // Style for the email input section
  const MainStyleBoxEmail = {
    textAlign: 'start',
    display: 'flex',
    flexDirection: 'column',
    marginTop: '17px',
  };

  // Styled arrow icon for the send button
  const ArrowForwardSharpIconStyled = styled(ArrowForwardSharpIcon)(() => ({
    color: theme.palette.mode == 'light' ? '#66FCFC' : '#18768C',
  }));

  // Style for the send button
  const StyledSignIn = {
    backgroundColor: theme.palette.mode == 'light' ? '#334255' : '#F1F1F1',
    color: theme.palette.mode == 'light' ? '#FFFFFF' : '#002136',
    marginTop: '20px',
  };

  // Style for the wrapper around the send button
  const StyledForgetWrapper = {
    textAlign: 'left',
    marginTop: '20px',
  };

  // Choose the background style based on the current theme
  const backColor = (theme.palette.mode === 'light') ? { ...backgroundTopRight } : { ...backgroundTopRightDark };

  /**
   * Handles the send button click event.
   * Validates the email, shows errors if any, and calls the API to send the reset link.
   * Navigates to reset confirmation on success.
   */
  const handleSubmit = (event: { preventDefault: () => void }) => {
    reactGAEvents('forget-password-verify', 'send');
    event.preventDefault();
    // Validate the email field
    const isValidField = validateFields.validate(
      {
        email: email
      },
      validationOptions
    );

    if (isValidField?.error) {
      // If validation fails, set error messages
      let errorKeys: any = { email: '' };
      isValidField?.error?.details?.forEach((val) => {
        if (val?.context?.key === 'email') {
          errorKeys.email = val?.message;
        }
      });

      setErrors(errorKeys);
      return;
    }
    // If valid, show loader and call API
    setLoading(true);
    Api.forgetPasswordEmail({ email })
      .then((res) => {
        if (res.success) {
          setLoading(false);
          // Navigate to reset confirmation page
          navigate('/reset-activated');
        } else {
          // Show API errors
          const { data }:any = res;
          setLoading(false);
          setErrors(data);
        }
      })
      .catch((res) => {
        // Log error and hide loader
        console.log(res);
        setLoading(false);
      });
  };

  // On mount, redirect to home if already authorized
  useEffect(() => {
    let isAuth = Api.isAuthorized();
    if (isAuth) navigate('/home');
  }, []);

  return (
    <>
      {/* Normalize CSS for consistent look */}
      <CssBaseline />
      {/* App header (logo, nav, etc.) */}
      <Header />
      {/* Show loader overlay if loading */}
      {loading && <Loader />}
      {/* Main background section with theme-based color/gradient */}
      <Box sx={{ ...backColor, display: 'grid' }}>
        <Box sx={styledBoxMainWrapper(isMobile, themeMode)}>
          <Container maxWidth="lg">
            <Box sx={styledDividerLeft(isMobile, theme)}>
              <Box>
                {/* Main content: title and instructions */}
                <Box sx={MainStyledBox}>
                  <Typography variant="h2" sx={titleStyle(isMobile, themeMode)}>
                    Forgot password
                  </Typography>
                  <Typography variant="h2" sx={styledText(themeMode)}>
                    Enter the email address associated with your account and we’ll send you a link to
                    reset your password.
                  </Typography>
                </Box>
                {/* Email input field */}
                <Box sx={MainStyleBoxEmail}>
                  <HDTextField
                    type="text"
                    lblText="Email"
                    onChange={({ target }: any) => setEmail(target.value)}
                    onClick={() => reactGAEvents('forget-password-verify', 'email')}
                    error={errors ? errors.email : false}
                    errorMsg={errors ? errors.email : null}
                    value={email}
                    id="forgot-password-email"
                  />
                </Box>
                {/* Send button */}
                <Box sx={StyledForgetWrapper}>
                  <Box>
                    <HDButton
                      variant={'contained'}
                      sx={StyledSignIn}
                      endIcon={<ArrowForwardSharpIconStyled />}
                      text={'Send'}
                      onClick={(e) => handleSubmit(e)}
                      showAnimation={true}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Container>
        </Box>
      </Box>
      {/* App footer (copyright, links, etc.) */}
      <Footer />
    </>
  );
};

export default ForgetPassword;
